<script setup>
import {ref, watch} from "vue";
import html2canvas from "html2canvas";
import axios from "axios";

const chatOpen = ref(false);

const chatId = ref();

const aiThinking = ref(false);

const messages = ref([]);

const userMessage = ref('');
const input = ref(null)

const lastMessageRef = ref(null);

const screenshot = ref();

const toggleChat = () => {
  chatOpen.value = !chatOpen.value;
}

const pushAiMessage = (content) => {
  messages.value.push({
    content,
    role: 'system',
    id: (Math.random() + 1).toString(36).substring(7),
  });
  aiThinking.value = false;
  scrollToLastMessage();
  focusInput();
}

const pushUserMessage = async () => {
  if (!userMessage.value) return;

  const content = userMessage.value;
  messages.value.push({
    content,
    role: 'user',
    id: (Math.random() + 1).toString(36).substring(7),
  });
  scrollToLastMessage();

  aiThinking.value = true;
  userMessage.value = '';
  await takeScreenshot()

  const payload = {
    content,
    screenshot: screenshot.value,
  };

  const endpoint = chatId.value ? `ajax/v2/ai-assistant/${chatId.value}/message` : '/ajax/v2/ai-assistant/chat';

  try {
    const response = await axios.post(endpoint, payload);
    chatId.value = response.data.data.id;
    messages.value = response.data.data.messages;
  } catch (e) {
    pushAiMessage('Sorry, something went wrong. Please try again.')
  }
  aiThinking.value = false;

  scrollToLastMessage();
  focusInput();
}

const scrollToLastMessage = () => {
  window.setTimeout(() => {
    lastMessageRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    }, 200);
  })
};

const focusInput = () => {
  input.value.focus();
}

const takeScreenshot = async () => {
  const canvas = await html2canvas(document.body);
  // Original dimensions
  const originalWidth = canvas.width;
  const originalHeight = canvas.height;

  // Desired dimensions
  const newWidth = Math.min(900, originalWidth);
  const newHeight = (originalHeight * newWidth) / originalWidth;

  // Create a new canvas to draw the resized image
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = newWidth;
  resizedCanvas.height = newHeight;

  const ctx = resizedCanvas.getContext('2d');
  // Draw the image at the new size
  ctx.drawImage(canvas, 0, 0, originalWidth, originalHeight, 0, 0, newWidth, newHeight);

  document.body.appendChild(resizedCanvas);

  screenshot.value = resizedCanvas.toDataURL('image/png', 0.75);

  document.body.removeChild(resizedCanvas);
}

watch(chatOpen, function () {
  if (!chatOpen.value || messages.value.length > 0) return;

  // Send welcome message
  aiThinking.value = true;
  window.setTimeout(() => {
    pushAiMessage("Hello, I'm your AI assistant, how can I help?");
  }, 1500);
});
</script>

<template>
  <div class="tw-fixed tw-bottom-2 tw-left-2 tw-z-50">
    <div
      v-show="chatOpen"
      class="tw-bg-white tw-p-4 tw-rounded-lg tw-w-[400px] tw-max-w-[90vw] tw-shadow-2xl tw-mb-2"
    >
      <div class="tw-h-[300px] tw-max-h-[70vh] tw-overflow-y-auto tw-flex tw-flex-col tw-gap-2 tw-mb-2">
        <div
          v-for="message in messages"
          :key="message.id"
          class="tw-rounded-lg tw-p-2 tw-border tw-text-white tw-text-sm"
          :class="{
            'tw-ml-6 tw-bg-green-400': message.role === 'user',
            'tw-mr-6 tw-bg-blue-400': message.role === 'system',
          }"
        >
          {{ message.content }}
        </div>
        <div
          ref="lastMessageRef"
          class="tw-h-1"
        />
        <div
          v-show="aiThinking"
          class="tw-text-grey tw-text-sm tw-mt-auto tw-flex"
        >
          <svg-vue
            icon="icons/sparkles-outline"
            class="tw-h-3 tw-w-3 tw-mr-1"
          />
          <span>AI Assistant is typing...</span>
        </div>
      </div>
      <div>
        <form
          action=""
          @submit.prevent="pushUserMessage"
        >
          <input
            ref="input"
            v-model="userMessage"
            type="text"
            class="tw-border tw-p-2 tw-w-full tw-rounded-sm"
            placeholder="Type your message here..."
          >
        </form>
      </div>
    </div>

    <button
      class="tw-rounded-full tw-bg-red-500 tw-shadow-lg tw-p-4"
      @click.prevent="toggleChat"
    >
      <svg-vue
        v-if="!chatOpen"
        icon="icons/sparkles-outline"
        class="tw-text-white tw-h-4 tw-w-4"
      />
      <svg-vue
        v-if="chatOpen"
        icon="icons/close"
        class="tw-text-white tw-h-4 tw-w-4"
      />
    </button>
  </div>
</template>

<style scoped>

</style>